// src/pages/PortfolioAnalysis.jsx
import React, { useContext } from "react";
import { NavLink, Outlet } from "react-router-dom";
import Navbar from "../components/Navbar";
import TierBadge from "../components/gating/TierBadge";
import { AuthContext } from "../context/AuthContext";
import { meetsFeature, featureMinTier } from "../config/tiers";

// Sub-tabs shown directly under the global Navbar. Linked Accounts is
// intentionally excluded — it's reachable via Settings > Connected accounts.
// `feature` marks a tier-gated tab; a lock badge shows when the user lacks it.
const TABS = [
  { to: "/portfolio",                 label: "Overview",        end: true,  feature: null },
  { to: "/portfolio/performance",     label: "Performance",     end: false, feature: "performance" },
  { to: "/portfolio/holdings",        label: "Holdings",        end: false, feature: null },
  { to: "/portfolio/recommendations", label: "Recommendations", end: false, feature: "recommendations" },
  { to: "/portfolio/tax",             label: "Tax",             end: false, feature: "tax_harvest" },
];

const PortfolioAnalysis = () => {
  const { profile } = useContext(AuthContext);
  const tier = profile?.tier || "basic";

  return (
    <div
      className="
        min-h-screen pt-28
        bg-gray-50 text-gray-900
        dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black
        dark:text-white
      "
    >
      {/* Top Navbar — fixed top-4, ~64px tall. */}
      <Navbar />

      {/* SUB-TAB BAR — centered pill under the navbar. Sticky so it stays
          pinned 96px from the viewport top as the user scrolls, mirroring
          the floating navbar's chrome treatment (backdrop blur + border).
          On narrow screens the tabs WRAP onto multiple rows (no horizontal
          scrolling); on desktop they sit in a single centered row.
          Spacing for the fixed navbar lives on the wrapper as pt-28
          (padding, not margin) — margin-top on the first child would
          collapse out of the wrapper and expose the body color above. */}
      <div className="sticky top-24 z-30">
        <div className="flex justify-center px-4">
          <div
            className="
              flex flex-wrap justify-center items-center gap-1 p-1 rounded-2xl max-w-full
              backdrop-blur-xl border shadow-glass
              bg-white/70 border-gray-200
              dark:bg-[#0f1624]/70 dark:border-white/10 dark:shadow-glass-dark
            "
          >
            {TABS.map(({ to, label, end, feature }) => {
              const locked = feature && !meetsFeature(tier, feature);
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? "nav-glass-active"
                        : "text-gray-500 dark:text-gray-300 hover:bg-gray-200/60 dark:hover:bg-white/5"
                    }`
                  }
                >
                  <span className="relative z-10 inline-flex items-center gap-1.5">
                    {label}
                    {locked && <TierBadge tier={featureMinTier(feature)} />}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>

      {/* CONTENT AREA — sits under the sticky tab bar. */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 pt-8 pb-12 animate-fadeIn">
        <Outlet />
      </div>
    </div>
  );
};

export default PortfolioAnalysis;
